import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown } from '@carbon/react';
import { Maximize } from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ExpandableContainer } from 'components/expandable-container';
import {
  ProbitChart,
  type ProbitTooltipRenderer,
  type Stream,
  type YAxisMode,
} from 'components/charts/probit-chart';
import { formatNumber } from 'utils/number';
import type {
  TypeCurveCumulativeYears,
  TypeCurveDcaParamKey,
  TypeCurveProbitMetric,
  TypeCurveProbitParamKey,
  TypeCurveProbitRequest,
  TypeCurveProbitResponse,
  TypeCurveRatioMetric,
} from 'types/type-curve-probit';
import {
  typeCurveProbitMutationAtoms,
  TYPE_CURVE_PROBIT_URL,
  tcForecastOverrideAtom,
  tcLateralLengthFtAtom,
  probitLastDispatchKey,
  tcProbitOverrideAtoms,
  probitRefreshTriggerAtom,
} from '../atoms';
import { computeTypeCurveValues } from '../type-curve-dca/compute-probit-type-curve-values';
import {
  probitPlotChartAtom,
  tcProbitColorMapAtom,
  tcWellsLoadingAtom,
} from '../type-curve-filters/atoms';
import type { FilteredWell } from '../types';
import styles from './type-curve-probit-plots.module.scss';

const STREAMS: Stream[] = ['oil', 'gas', 'water'];

const STREAM_CFG: Record<Stream, { title: string; color: string }> = {
  oil: { title: 'Probit Plot - Oil', color: '#42be65' },
  gas: { title: 'Probit Plot - Gas', color: '#fa4d56' },
  water: { title: 'Probit Plot - Water', color: '#4589ff' },
};

// ---------------------------------------------------------------------------
// UI metric — the umbrella 'ratios' groups the individual ratio-based metrics
// under a secondary dropdown (mirroring how 'dca_params' opens a param-key
// sub-dropdown). The API-level metric is derived from `uiMetric` + the
// chosen ratio at request time.
// ---------------------------------------------------------------------------
type UiMetric =
  'eur' | 'eur_per_ft' | 'scaled_eur' | 'dca_params' | 'cumulative' | 'ratios';

type DropdownItem<T extends string = string> = { id: T; text: string };

const METRIC_ITEMS: DropdownItem<UiMetric>[] = [
  { id: 'eur', text: 'EUR' },
  { id: 'eur_per_ft', text: 'EUR per ft' },
  { id: 'scaled_eur', text: 'Scaled EUR' },
  { id: 'dca_params', text: 'DCA Parameters' },
  { id: 'cumulative', text: 'Cumulatives' },
  { id: 'ratios', text: 'Ratios' },
];

const DCA_PARAM_ITEMS: DropdownItem<TypeCurveDcaParamKey>[] = [
  { id: 'qi', text: 'qi' },
  { id: 'di', text: 'di' },
  { id: 'b_factor', text: 'b' },
  { id: 'dlim', text: 'dlim' },
];

const CUMULATIVE_YEARS_ITEMS: DropdownItem<string>[] = [
  { id: '1', text: '1 year' },
  { id: '3', text: '3 years' },
  { id: '5', text: '5 years' },
  { id: '10', text: '10 years' },
];

const RATIO_ITEMS: DropdownItem<TypeCurveRatioMetric>[] = [
  { id: 'gor', text: 'GOR' },
  { id: 'cgr', text: 'CGR' },
  { id: 'wor', text: 'WOR' },
  { id: 'wgr', text: 'WGR' },
  { id: 'watercut', text: 'Water Cut' },
];

const Y_MODE_ITEMS: DropdownItem<YAxisMode>[] = [
  { id: 'percentile', text: 'Percentile' },
  { id: 'probit', text: 'Probit' },
];

const UI_METRIC_AXIS_LABELS: Record<UiMetric, string> = {
  eur: 'EUR',
  eur_per_ft: 'EUR per ft',
  scaled_eur: 'Scaled EUR',
  dca_params: 'DCA Parameters',
  cumulative: 'Cumulative',
  ratios: 'Ratio',
};

const DCA_PARAM_LABELS: Record<TypeCurveDcaParamKey, string> = {
  qi: 'qi',
  di: 'di',
  b_factor: 'b',
  dlim: 'dlim',
};

const RATIO_LABELS: Record<TypeCurveRatioMetric, string> = {
  gor: 'GOR',
  cgr: 'CGR',
  wor: 'WOR',
  wgr: 'WGR',
  watercut: 'Water Cut',
};

const STREAM_DISPLAY: Record<Stream, string> = {
  oil: 'Oil',
  gas: 'Gas',
  water: 'Water',
};

function buildTooltipMetricLabel(
  uiMetric: UiMetric,
  stream: Stream,
  paramKey: TypeCurveDcaParamKey,
  ratio: TypeCurveRatioMetric,
  cumulativeYears: TypeCurveCumulativeYears
): string {
  const s = STREAM_DISPLAY[stream];
  switch (uiMetric) {
    case 'eur':
      return `${s} EUR`;
    case 'eur_per_ft':
      return `${s} EUR per ft`;
    case 'scaled_eur':
      return `Scaled ${s} EUR`;
    case 'cumulative':
      return `${cumulativeYears}-yr Cumulative ${s}`;
    case 'dca_params':
      return DCA_PARAM_LABELS[paramKey];
    case 'ratios':
      return RATIO_LABELS[ratio];
    default:
      return s;
  }
}

function itemToString<T extends string>(item: DropdownItem<T> | null): string {
  return item ? item.text : '';
}

function buildRequestFields(
  uiMetric: UiMetric,
  ratio: TypeCurveRatioMetric,
  paramKey: TypeCurveDcaParamKey
): { metric: TypeCurveProbitMetric; paramKey?: TypeCurveProbitParamKey } {
  if (uiMetric === 'ratios') return { metric: 'ratio', paramKey: ratio };
  if (uiMetric === 'dca_params') return { metric: 'dca_params', paramKey };
  return { metric: uiMetric };
}

// ---------------------------------------------------------------------------
// Per-stream card — owns its own metric/param/ratio/yMode/axisSwapped state
// and its own mutation atom subscription. Mirrors ProbitPlotCard in
// pages/dca-workspace/probit-plots.tsx.
// ---------------------------------------------------------------------------

interface TypeCurveProbitPlotCardProps {
  stream: Stream;
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
  wells?: FilteredWell[];
  uwiColorMap?: Record<string, string>;
  highlightedUwi?: string | null;
  onPointHover?: (uwi: string | null) => void;
}

const TypeCurveProbitPlotCard: FC<TypeCurveProbitPlotCardProps> = ({
  stream,
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  wells,
  uwiColorMap,
  highlightedUwi,
  onPointHover,
}) => {
  const [uiMetric, setUiMetric] = useState<UiMetric>('scaled_eur');
  const [paramKey, setParamKey] = useState<TypeCurveDcaParamKey>('qi');
  const [cumulativeYears, setCumulativeYears] =
    useState<TypeCurveCumulativeYears>(3);
  const [ratio, setRatio] = useState<TypeCurveRatioMetric>('gor');
  const [yMode, setYMode] = useState<YAxisMode>('percentile');
  const [axisSwapped, setAxisSwapped] = useState(false);

  const forecastOverride = useAtomValue(tcForecastOverrideAtom);
  const lateralLengthFt = useAtomValue(tcLateralLengthFtAtom);
  const probitRefreshTrigger = useAtomValue(probitRefreshTriggerAtom);

  const [{ loading: probitLoading, result }, dispatch] = useAtom(
    typeCurveProbitMutationAtoms[stream]
  );
  const setProbitOverride = useSetAtom(tcProbitOverrideAtoms[stream]);

  // Sync successful fetch results to the shared override atom (mirrors
  // volume charts' useEffect → setSharedProductionWells pattern).
  useEffect(() => {
    if (result?.type === 'success') {
      setProbitOverride(result.data);
    }
  }, [result, setProbitOverride]);

  const wellsLoading = useAtomValue(tcWellsLoadingAtom);
  const loading = probitLoading || wellsLoading;

  // Derive both the string UWI list (for the API payload) and the well meta
  // map (for tooltip well name + qualifier) directly from the filtered wells
  // prop — the probit endpoint itself doesn't return name/qualifier.
  const uwiList = useMemo(() => wells?.map((w) => w.uwi) ?? [], [wells]);
  const wellMetaByUwi = useMemo(() => {
    const map: Record<string, { name?: string; qualifier?: string }> = {};
    for (const well of wells ?? []) {
      if (!well?.uwi) continue;
      map[well.uwi] = {
        name: well.name ?? undefined,
        qualifier: well.wellQualifier ?? undefined,
      };
    }
    return map;
  }, [wells]);

  const { metric: apiMetric, paramKey: apiParamKey } = buildRequestFields(
    uiMetric,
    ratio,
    paramKey
  );

  // Fire the mutation whenever the request inputs settle. We wait until the
  // filtered wells list is populated — the filtered-wells atom publishes an
  // initial `[]` before the wells query resolves, which would otherwise send
  // a wasted pre-load request with no `uwis` (the TC contract requires either
  // `uwis` or `workspaceId`) and result in 2 calls per stream instead of 1.
  const uwisKey = useMemo(() => uwiList.join(','), [uwiList]);

  // Stream-specific key: compute the typeCurveValues for THIS stream and
  // derive a stable string. When only oil EUR changes, the gas/water keys
  // stay the same → those probit effects don't fire (no redundant loading).
  const typeCurveValuesKey = useMemo(() => {
    if (!forecastOverride) return 'null';
    const tcv = computeTypeCurveValues(
      forecastOverride,
      apiMetric,
      typeCurveHeaderDetailsId,
      {
        paramKey: apiParamKey,
        cumulativeYears:
          apiMetric === 'cumulative' ? cumulativeYears : undefined,
        lateralLengthFt,
      }
    );
    if (!tcv) return 'null';
    // For stream-keyed metrics (eur, scaled_eur, eur_per_ft, dca_params,
    // cumulative) we only track THIS stream's value. When the override has
    // no data for this stream, treat it as 'null' so the key doesn't change.
    // For ratio metrics the keys are ratio names (gor, wor…) — use the full
    // object serialization.
    const sv = tcv[stream];
    if (sv !== undefined) return String(sv);
    if (apiMetric === 'ratio') return JSON.stringify(tcv);
    return 'null';
  }, [
    forecastOverride,
    apiMetric,
    typeCurveHeaderDetailsId,
    apiParamKey,
    cumulativeYears,
    lateralLengthFt,
    stream,
  ]);

  useEffect(() => {
    if (!typeCurveHeaderDetailsId) return;
    if (uwiList.length === 0) return;

    // Build a dispatch key from every input that affects the payload for THIS
    // stream. Shared across components via probitLastDispatchKey so the DCA
    // modal's dispatch is visible here — if the key matches and the atom
    // already has a result, we skip and avoid a redundant loading flash.
    const pk = apiParamKey !== undefined ? apiParamKey : '';
    const cy = apiMetric === 'cumulative' ? cumulativeYears : '';
    const dispatchKey =
      `${apiMetric}:${pk}:${cy}:${typeCurveHeaderDetailsId}:` +
      `${dcaScenarioId}:${uwisKey}:${typeCurveValuesKey}:${lateralLengthFt}:${probitRefreshTrigger}`;

    if (
      dispatchKey === probitLastDispatchKey[stream] &&
      (result?.type === 'success' ||
        (probitOverride && probitOverride.metric === apiMetric))
    ) {
      return;
    }

    const typeCurveValues = computeTypeCurveValues(
      forecastOverride,
      apiMetric,
      typeCurveHeaderDetailsId,
      {
        paramKey: apiParamKey,
        cumulativeYears:
          apiMetric === 'cumulative' ? cumulativeYears : undefined,
        lateralLengthFt,
      }
    );

    const payload: TypeCurveProbitRequest = {
      dcaScenarioId,
      typeCurveHeaderDetailsId: typeCurveHeaderDetailsId,
      metric: apiMetric,
      uwis: uwiList,
      useTcScenarioScaling:
        import.meta.env.VITE_TYPE_CURVE_SCENARIO_SCALING_ENABLED === 'true',
      ...(apiParamKey !== undefined ? { paramKey: apiParamKey } : {}),
      ...(apiMetric === 'cumulative' ? { cumulativeYears } : {}),
      ...(typeCurveValues && { typeCurveValues }),
      responseConfig: {
        includeTypeCurvePValue: true,
        includeLatestPromotedForecastOnly: true,
      },
    };

    probitLastDispatchKey[stream] = dispatchKey;
    dispatch({
      url: TYPE_CURVE_PROBIT_URL,
      type: 'POST',
      data: payload,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apiMetric,
    apiParamKey,
    cumulativeYears,
    typeCurveHeaderDetailsId,
    dcaScenarioId,
    uwisKey,
    dispatch,
    typeCurveValuesKey,
    lateralLengthFt,
    stream,
    probitRefreshTrigger,
  ]);

  // Prefer override when its metric matches (covers modal-saved data
  // with golden line); fall back to mutation atom for loading/error states.
  const probitOverride = useAtomValue(tcProbitOverrideAtoms[stream]);
  const data: TypeCurveProbitResponse | null =
    probitOverride && probitOverride.metric === apiMetric
      ? probitOverride
      : result?.type === 'success'
        ? result.data
        : null;

  // Stream-based metrics pull from `streams[stream]`; ratios live in `ratio`.
  // `typeCurvePValues` is keyed by stream for stream metrics and by the ratio
  // metric name for ratio metrics — mirror the same selector. The TC
  // annotation and per-well metadata ride along on the same object so the
  // chart gets all plot inputs from a single `data` prop.
  const chartData = useMemo(() => {
    if (!data) return null;
    const base = data.streams ? data.streams[stream] : data.ratio;
    if (!base) return null;

    const tcEntry = data.typeCurvePValues
      ? data.streams
        ? data.typeCurvePValues[stream]
        : data.typeCurvePValues[ratio]
      : null;
    const typeCurve = tcEntry
      ? {
          value: tcEntry.value,
          pValue: tcEntry.pValue,
          label: tcEntry.label,
        }
      : null;

    return { ...base, typeCurve, wellMetaByUwi };
  }, [data, stream, ratio, wellMetaByUwi]);

  const xAxisLabel = useMemo(() => {
    const unit = chartData?.unit;
    let base: string;
    if (uiMetric === 'dca_params') base = DCA_PARAM_LABELS[paramKey];
    else if (uiMetric === 'ratios') base = RATIO_LABELS[ratio];
    else if (uiMetric === 'cumulative')
      base = `${cumulativeYears}-yr Cumulative`;
    else base = UI_METRIC_AXIS_LABELS[uiMetric];
    return unit ? `${base} [${unit}]` : base;
  }, [uiMetric, paramKey, ratio, cumulativeYears, chartData]);

  // TC-specific tooltip: swaps the last row between "Probit" and
  // "Probability of Exceedance" based on the Y-axis dropdown, and prefixes
  // each row with Well Name / UWI / Well Qualifier. Metric label mirrors the
  // UI selection (e.g. "Scaled Gas EUR", "WGR", "qi"); unit is pulled from
  // the response so it stays in sync with ratio / cumulative variants.
  const tooltipMetricLabel = useMemo(
    () =>
      buildTooltipMetricLabel(
        uiMetric,
        stream,
        paramKey,
        ratio,
        cumulativeYears
      ),
    [uiMetric, stream, paramKey, ratio, cumulativeYears]
  );
  const tooltipRenderer: ProbitTooltipRenderer = useMemo(
    () => (ctx, cls) => {
      // All displayed values come straight from the probit response
      // (rawValue, poe, zScore, unit) and the TC wells atom (wellName,
      // wellQualifier). Nothing is computed client-side — any missing field
      // renders as `--` so the popup never shows fabricated numbers.
      const wellName = ctx.wellName || '--';
      const qualifier = ctx.wellQualifier || '--';
      const uwi = ctx.uwi || '--';
      const formattedValue =
        typeof ctx.rawValue === 'number'
          ? formatNumber(ctx.rawValue, 0, 2)
          : '--';
      const unitSuffix = ctx.unit ? ` ${ctx.unit}` : '';
      const metricDisplay =
        typeof ctx.rawValue === 'number'
          ? `${formattedValue}${unitSuffix}`
          : '--';

      let probabilityRow: string;
      if (ctx.yMode === 'probit') {
        const z =
          typeof ctx.zScore === 'number'
            ? formatNumber(ctx.zScore, 2, 2)
            : '--';
        probabilityRow = `<div class="${cls.row}"><span class="${cls.label}">Probit:</span> <span class="${cls.value}">${z}</span></div>`;
      } else {
        const poe =
          typeof ctx.poe === 'number'
            ? `${formatNumber(ctx.poe * 100, 2, 2)}%`
            : '--';
        probabilityRow = `<div class="${cls.row}"><span class="${cls.label}">Probability of Exceedance:</span> <span class="${cls.value}">${poe}</span></div>`;
      }

      return (
        `<div class="${cls.row}"><span class="${cls.label}">Well Name:</span> <span class="${cls.value}">${wellName}</span></div>` +
        `<div class="${cls.row}"><span class="${cls.label}">UWI:</span> <span class="${cls.value}">${uwi}</span></div>` +
        `<div class="${cls.row}"><span class="${cls.label}">Well Qualifier:</span> <span class="${cls.value}">${qualifier}</span></div>` +
        `<div class="${cls.row}"><span class="${cls.label}">${tooltipMetricLabel}:</span> <span class="${cls.value}">${metricDisplay}</span></div>` +
        probabilityRow
      );
    },
    [tooltipMetricLabel]
  );

  const renderHeader = (isExpanded: boolean, expand: () => void) => (
    <div className={styles.chartTitleRow}>
      <span className={styles.chartTitle}>{STREAM_CFG[stream].title}</span>
      {!isExpanded && (
        <button
          type="button"
          className={styles.chartExpandButton}
          onClick={expand}
          aria-label={`Expand ${STREAM_CFG[stream].title}`}
        >
          <Maximize size={16} />
        </button>
      )}
    </div>
  );

  const footer = (
    <div className={styles.chartFooter}>
      <div className={styles.footerLeft}>
        <span className={styles.footerLabel}>X-axis:</span>
        <Dropdown
          id={`tc-metric-${stream}`}
          size="sm"
          direction="top"
          titleText=""
          label="Select Parameter"
          items={METRIC_ITEMS}
          itemToString={itemToString}
          selectedItem={METRIC_ITEMS.find((i) => i.id === uiMetric) ?? null}
          onChange={({
            selectedItem,
          }: {
            selectedItem?: DropdownItem<UiMetric> | null;
          }) => {
            if (selectedItem) setUiMetric(selectedItem.id);
          }}
          className={styles.carbonDropdown}
          disabled={loading}
        />

        {uiMetric === 'dca_params' && (
          <Dropdown
            id={`tc-dca-param-${stream}`}
            size="sm"
            direction="top"
            titleText=""
            label="Select Param"
            items={DCA_PARAM_ITEMS}
            itemToString={itemToString}
            selectedItem={
              DCA_PARAM_ITEMS.find((i) => i.id === paramKey) ?? null
            }
            onChange={({
              selectedItem,
            }: {
              selectedItem?: DropdownItem<TypeCurveDcaParamKey> | null;
            }) => {
              if (selectedItem) setParamKey(selectedItem.id);
            }}
            className={styles.carbonDropdown}
            disabled={loading}
          />
        )}

        {uiMetric === 'cumulative' && (
          <Dropdown
            id={`tc-cumulative-years-${stream}`}
            size="sm"
            direction="top"
            titleText=""
            label="Select Years"
            items={CUMULATIVE_YEARS_ITEMS}
            itemToString={itemToString}
            selectedItem={
              CUMULATIVE_YEARS_ITEMS.find(
                (i) => i.id === String(cumulativeYears)
              ) ?? null
            }
            onChange={({
              selectedItem,
            }: {
              selectedItem?: DropdownItem<string> | null;
            }) => {
              if (selectedItem)
                setCumulativeYears(
                  Number(selectedItem.id) as TypeCurveCumulativeYears
                );
            }}
            className={styles.carbonDropdown}
            disabled={loading}
          />
        )}

        {uiMetric === 'ratios' && (
          <Dropdown
            id={`tc-ratio-${stream}`}
            size="sm"
            direction="top"
            titleText=""
            label="Select Ratio"
            items={RATIO_ITEMS}
            itemToString={itemToString}
            selectedItem={RATIO_ITEMS.find((i) => i.id === ratio) ?? null}
            onChange={({
              selectedItem,
            }: {
              selectedItem?: DropdownItem<TypeCurveRatioMetric> | null;
            }) => {
              if (selectedItem) setRatio(selectedItem.id);
            }}
            className={styles.carbonDropdown}
            disabled={loading}
          />
        )}
      </div>

      <div className={styles.footerRight}>
        <span className={styles.footerLabel}>Y-axis:</span>
        <Dropdown
          id={`tc-y-mode-${stream}`}
          size="sm"
          direction="top"
          titleText=""
          label="Select Y-Axis"
          items={Y_MODE_ITEMS}
          itemToString={itemToString}
          selectedItem={Y_MODE_ITEMS.find((i) => i.id === yMode) ?? null}
          onChange={({
            selectedItem,
          }: {
            selectedItem?: DropdownItem<YAxisMode> | null;
          }) => {
            if (selectedItem) setYMode(selectedItem.id);
          }}
          className={styles.carbonDropdown}
          disabled={loading}
        />
        <Button
          kind="primary"
          size="sm"
          onClick={() => setAxisSwapped((prev) => !prev)}
          disabled={loading}
          className={styles.swapBtn}
        >
          Swap
        </Button>
      </div>
    </div>
  );

  return (
    <ExpandableContainer>
      {({ isExpanded, expand }) => (
        <div className={styles.chartWrapper}>
          <ProbitChart
            data={chartData}
            uwis={data?.uwis ?? []}
            yMode={yMode}
            axisSwapped={axisSwapped}
            color={STREAM_CFG[stream].color}
            xAxisLabel={xAxisLabel}
            loading={loading}
            header={renderHeader(isExpanded, expand)}
            footer={footer}
            exportConfig={
              // No export trigger in the expanded view — it would collide with
              // the expand overlay's close button.
              isExpanded
                ? undefined
                : {
                    label: `${STREAM_CFG[stream].title} Probit`,
                    filenamePrefix: `type-curve-probit-${stream}`,
                  }
            }
            tooltipRenderer={tooltipRenderer}
            uwiColorMap={uwiColorMap}
            highlightedUwi={highlightedUwi}
            onPointHover={onPointHover}
          />
        </div>
      )}
    </ExpandableContainer>
  );
};

// ---------------------------------------------------------------------------
// Parent — renders one card per stream.
// ---------------------------------------------------------------------------

interface TypeCurveProbitPlotsProps {
  /** Required by the probit API for TC scaling + P-value context. */
  typeCurveHeaderDetailsId: number | null;
  /**
   * Required by the probit API. TC pages don't currently surface a scenario
   * picker; callers should pass the active / default scenario ID.
   */
  dcaScenarioId: number;
  /**
   * Optional pre-resolved filtered wells (TC pages filter upstream via
   * POST /queries/wells). Each entry carries `uwi` + `name` + `wellQualifier`
   * so probit tooltips can render well context without a separate lookup.
   * When omitted the backend falls back to workspace/UWI-free resolution.
   */
  wells?: FilteredWell[];
  /** UWI to highlight across all stream cards (lifted from parent). */
  highlightedUwi?: string | null;
  /** Fired when the user hovers over / leaves a point (lifted to parent). */
  onPointHover?: (uwi: string | null) => void;
}

export const TypeCurveProbitPlots: FC<TypeCurveProbitPlotsProps> = ({
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  wells,
  highlightedUwi,
  onPointHover,
}) => {
  const streamVisibility = useAtomValue(probitPlotChartAtom);
  const visibleStreams = STREAMS.filter((s) => streamVisibility[s]);
  const uwiColorMap = useAtomValue(tcProbitColorMapAtom);

  return (
    <>
      {visibleStreams.map((stream) => (
        <TypeCurveProbitPlotCard
          key={stream}
          stream={stream}
          typeCurveHeaderDetailsId={typeCurveHeaderDetailsId}
          dcaScenarioId={dcaScenarioId}
          wells={wells}
          uwiColorMap={uwiColorMap}
          highlightedUwi={highlightedUwi ?? null}
          onPointHover={onPointHover}
        />
      ))}
    </>
  );
};
